import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Gauge, Fuel, Calendar, Settings2, Palette, Zap, Car, ArrowUpRight, ShieldCheck, BadgeCheck, Expand, Share2, Link2 } from "lucide-react";
import { api, fileUrl, euro, km } from "../lib/api";
import { CarCard } from "../components/CarCard";
import { ImageLightbox } from "../components/ImageLightbox";
import { waLink, WhatsAppIcon } from "../components/WhatsApp";

export default function CarDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [car, setCar] = useState(null);
  const [others, setOthers] = useState([]);
  const [active, setActive] = useState(0);
  const [notFound, setNotFound] = useState(false);
  const [lightbox, setLightbox] = useState(false);

  useEffect(() => {
    setActive(0);
    api.get(`/cars/${id}`).then((r) => setCar(r.data)).catch(() => setNotFound(true));
    api.get("/cars").then((r) => setOthers(r.data.filter((c) => c.id !== id).slice(0, 3))).catch(() => {});
    api.post(`/cars/${id}/view`).catch(() => {});
  }, [id]);

  // Per-occasion SEO: eigen titel, omschrijving en deelplaatje
  useEffect(() => {
    if (!car) return;
    const SITE = "https://stoofvinkeveen.nl";
    const setMeta = (name, content, attr = "name") => {
      if (!content) return;
      let m = document.querySelector(`meta[${attr}="${name}"]`);
      if (!m) { m = document.createElement("meta"); m.setAttribute(attr, name); document.head.appendChild(m); }
      m.setAttribute("content", content);
    };
    const bj = car.bouwjaar ? ` ${car.bouwjaar}` : "";
    const title = `${car.merk} ${car.model}${bj} — ${euro(car.prijs)} | Stoof Vinkeveen`;
    const specs = [car.bouwjaar, car.km ? km(car.km) : null, car.brandstof, car.transmissie].filter(Boolean).join(", ");
    const desc = `${car.merk} ${car.model} occasion te koop bij Automobielbedrijf Stoof in Vinkeveen${specs ? ` — ${specs}` : ""}. Prijs ${euro(car.prijs)}. Plan een proefrit of vraag informatie aan.`;
    const image = car.images?.[0] ? fileUrl(car.images[0]) : `${SITE}/og-occasions.jpg`;
    document.title = title;
    setMeta("description", desc);
    setMeta("og:title", title, "property");
    setMeta("og:description", desc, "property");
    setMeta("og:image", image, "property");
    setMeta("og:type", "product", "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", desc);
    setMeta("twitter:image", image);
  }, [car]);

  if (notFound) return (
    <div className="pt-40 pb-24 text-center min-h-screen">
      <p className="text-text-muted">Auto niet gevonden.</p>
      <Link to="/occasions" className="text-primary font-semibold mt-4 inline-block">← Terug naar occasions</Link>
    </div>
  );
  if (!car) return <div className="pt-40 pb-24 text-center min-h-screen text-text-muted">Laden...</div>;

  const specs = [
    { icon: Calendar, l: "Bouwjaar", v: car.bouwjaar },
    { icon: Gauge, l: "Kilometerstand", v: km(car.kilometerstand) },
    { icon: Fuel, l: "Brandstof", v: car.brandstof },
    { icon: Settings2, l: "Transmissie", v: car.transmissie },
    { icon: Zap, l: "Vermogen", v: car.vermogen || "—" },
    { icon: Palette, l: "Kleur", v: car.kleur || "—" },
    { icon: Car, l: "Carrosserie", v: car.carrosserie || "—" },
  ];

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareTitle = `${car.merk} ${car.model} — ${euro(car.prijs)}`;
  const shareText = `Bekijk deze ${car.merk} ${car.model} bij Automobielbedrijf Stoof: ${euro(car.prijs)}`;

  const trackShare = () => { api.post(`/cars/${car.id}/share`).catch(() => {}); };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: shareTitle, text: shareText, url: shareUrl });
        trackShare();
      } catch (_) { /* user cancelled */ }
    } else {
      copyLink();
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      trackShare();
      toast.success("Link gekopieerd naar klembord");
    } catch (_) {
      toast.error("Kopiëren mislukt — kopieer de link handmatig.");
    }
  };

  return (
    <div className="pt-28 pb-24 min-h-screen">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <button onClick={() => nav(-1)} data-testid="back-button" className="inline-flex items-center gap-2 text-sm font-semibold text-text-muted hover:text-primary transition-colors mb-8">
          <ArrowLeft size={16} /> Terug
        </button>

        <div className="grid lg:grid-cols-12 gap-10">
          {/* Gallery */}
          <div className="lg:col-span-7">
            <button
              type="button"
              onClick={() => car.images?.length && setLightbox(true)}
              data-testid="detail-main-image"
              className="group relative w-full aspect-[4/3] overflow-hidden bg-[#eef2f0] block cursor-zoom-in"
            >
              <motion.div
                key={active}
                initial={{ opacity: 0.4 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}
                className="w-full h-full"
              >
                {car.images?.[active] ? (
                  <img src={fileUrl(car.images[active])} alt={car.model} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full grid place-items-center text-text-muted">Geen foto beschikbaar</div>
                )}
              </motion.div>
              {car.images?.length > 0 && (
                <span className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-black/55 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1.5 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                  <Expand size={14} /> Vergroten
                </span>
              )}
            </button>
            {car.images?.length > 1 && (
              <div className="mt-4 grid grid-cols-5 gap-3">
                {car.images.map((img, i) => (
                  <button key={i} onClick={() => setActive(i)}
                    className={`aspect-[4/3] overflow-hidden border-2 transition-colors ${active === i ? "border-primary" : "border-transparent opacity-70 hover:opacity-100"}`}>
                    <img src={fileUrl(img)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="lg:col-span-5">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-primary">{car.merk}</p>
            <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tight mt-2 leading-none text-text-main">{car.model}</h1>
            {car.verkocht && <span className="inline-block mt-4 bg-text-main text-white text-xs font-bold tracking-widest uppercase px-3 py-1.5">Verkocht</span>}
            <p className="font-display font-black text-4xl text-primary mt-6">{euro(car.prijs)}</p>

            {car.kenteken && (
              <div className="mt-6 inline-flex items-stretch rounded-md overflow-hidden border-2 border-black/80 shadow-sm" data-testid="detail-kenteken">
                <div className="bg-[#1a3faa] text-white text-[9px] font-bold px-1.5 flex items-end justify-center pb-1 leading-none">NL</div>
                <div className="bg-[#f4cd00] text-black font-black tracking-[0.15em] text-2xl px-4 py-1.5">{car.kenteken}</div>
              </div>
            )}

            <div className="mt-8 grid grid-cols-2 gap-px bg-border border border-border">
              {specs.map((s) => (
                <div key={s.l} className="bg-background p-4">
                  <div className="flex items-center gap-2 text-text-muted text-xs font-semibold uppercase tracking-wide">
                    <s.icon size={14} /> {s.l}
                  </div>
                  <p className="font-display font-bold text-lg mt-1 text-text-main">{s.v}</p>
                </div>
              ))}
            </div>

            {(car.apk_vervaldatum || car.tellerstand_oordeel) && (
              <div className="mt-6 border border-border bg-background p-5" data-testid="detail-historie">
                <p className="text-xs font-bold uppercase tracking-wide text-primary mb-3 flex items-center gap-2">
                  <ShieldCheck size={15} /> Historie & keuring
                </p>
                <div className="space-y-2.5 text-sm">
                  {car.apk_vervaldatum && (
                    <div className="flex items-center justify-between">
                      <span className="text-text-muted">APK geldig tot</span>
                      <span className="font-semibold text-text-main">{car.apk_vervaldatum}</span>
                    </div>
                  )}
                  {car.tellerstand_oordeel && (
                    <div className="flex items-center justify-between">
                      <span className="text-text-muted">Tellerstand</span>
                      <span className="font-semibold text-text-main inline-flex items-center gap-1.5">
                        {/^logisch/i.test(car.tellerstand_oordeel) && <BadgeCheck size={16} className="text-emerald-600" />}
                        {car.tellerstand_oordeel}
                      </span>
                    </div>
                  )}
                </div>
                <p className="text-[11px] text-text-muted mt-3">Officiële gegevens · bron: RDW</p>
              </div>
            )}

            <Link to={`/contact?auto=${car.id}`} data-testid="detail-proefrit-btn"
              className="mt-8 w-full group inline-flex items-center justify-center gap-2 rounded-full bg-primary text-white font-semibold px-7 py-4 hover:bg-primary-light transition-colors">
              Proefrit aanvragen voor deze auto
              <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
            <a href="tel:0297261333" className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full border border-text-main/20 font-semibold px-7 py-4 hover:border-primary hover:text-primary transition-colors">
              Bel ons: 0297 - 261 333
            </a>

            {/* Deel deze auto */}
            <div className="mt-6 border-t border-border pt-5" data-testid="detail-share">
              <p className="text-xs font-bold uppercase tracking-wide text-text-muted mb-3">Deel deze auto</p>
              <div className="flex flex-wrap gap-3">
                <a
                  href={waLink(`${shareText}\n${shareUrl}`)}
                  target="_blank" rel="noreferrer" data-testid="share-whatsapp"
                  onClick={trackShare}
                  className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white font-semibold px-5 py-2.5 text-sm hover:brightness-95 transition-all"
                >
                  <WhatsAppIcon size={17} /> WhatsApp
                </a>
                <button
                  onClick={nativeShare}
                  data-testid="share-native"
                  className="inline-flex items-center gap-2 rounded-full border border-text-main/20 font-semibold px-5 py-2.5 text-sm hover:border-primary hover:text-primary transition-colors"
                >
                  <Share2 size={16} /> Delen
                </button>
                <button
                  onClick={copyLink}
                  data-testid="share-copy"
                  className="inline-flex items-center gap-2 rounded-full border border-text-main/20 font-semibold px-5 py-2.5 text-sm hover:border-primary hover:text-primary transition-colors"
                >
                  <Link2 size={16} /> Kopieer link
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Description */}
        {car.beschrijving && (
          <div className="mt-16 max-w-3xl border-t border-border pt-10">
            <h2 className="font-display font-bold text-2xl mb-4">Omschrijving</h2>
            <p className="text-text-muted leading-relaxed whitespace-pre-line">{car.beschrijving}</p>
          </div>
        )}

        {others.length > 0 && (
          <div className="mt-24">
            <h2 className="font-display font-bold text-3xl mb-10">Andere occasions</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {others.map((c, i) => <CarCard key={c.id} car={c} index={i} />)}
            </div>
          </div>
        )}
      </div>

      <ImageLightbox
        images={car.images || []}
        index={active}
        open={lightbox}
        onIndex={setActive}
        onClose={() => setLightbox(false)}
      />
    </div>
  );
}
